import { RefObject, useEffect } from 'react';
import { FormStatusTab } from './types';

const SCROLL_EDGE_THRESHOLD_PX = 2;

export function shouldShowActionsStickyEdgeCue(scrollEl: HTMLElement): boolean {
  const maxScrollLeft = scrollEl.scrollWidth - scrollEl.clientWidth;
  if (maxScrollLeft <= SCROLL_EDGE_THRESHOLD_PX) {
    return false;
  }
  const { scrollLeft } = scrollEl;
  const atScrollStart = scrollLeft <= SCROLL_EDGE_THRESHOLD_PX;
  const atScrollEnd = scrollLeft >= maxScrollLeft - SCROLL_EDGE_THRESHOLD_PX;
  return !atScrollStart && !atScrollEnd;
}

export function useFormManagementActionsColumnScrollActive(
  wrapperRef: RefObject<HTMLElement | null>,
  selectedTab: FormStatusTab,
  resyncKey: string,
) {
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) {
      return;
    }

    const testId = `forms-table-${selectedTab.toLowerCase()}-container`;
    const scrollEl = wrapper.querySelector<HTMLElement>(
      `[data-testid="${testId}"]`,
    );

    const sync = () => {
      if (!wrapper.isConnected) {
        return;
      }
      if (!scrollEl?.isConnected) {
        delete wrapper.dataset.formManagementActionsScroll;
        return;
      }
      if (shouldShowActionsStickyEdgeCue(scrollEl)) {
        wrapper.dataset.formManagementActionsScroll = 'true';
      } else {
        delete wrapper.dataset.formManagementActionsScroll;
      }
    };

    if (!scrollEl) {
      delete wrapper.dataset.formManagementActionsScroll;
      return;
    }

    sync();
    scrollEl.addEventListener('scroll', sync, { passive: true });
    const ro = new ResizeObserver(sync);
    ro.observe(scrollEl);

    return () => {
      scrollEl.removeEventListener('scroll', sync);
      ro.disconnect();
      delete wrapper.dataset.formManagementActionsScroll;
    };
  }, [wrapperRef, selectedTab, resyncKey]);
}
